'use client';

import React, { useState } from 'react';

export type ProductFilterOption = {
  label: string;
  value: string;
  count?: number;
};

export type ProductFilterSectionProps = {
  title: string;
  options: ProductFilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  type?: 'radio' | 'checkbox';
  className?: string;
  maxVisible?: number;
};

const ProductFilterSection: React.FC<ProductFilterSectionProps> = ({ title, options, selectedValues, onChange, type = 'checkbox', className = '', maxVisible = 6 }) => {
  const [showAll, setShowAll] = useState(false);
  const visibleOptions = showAll ? options : options.slice(0, maxVisible);
  const hasMore = options.length > maxVisible;

  const handleOptionChange = (value: string, checked: boolean) => {
    if (type === 'radio') {
      onChange(checked ? [value] : []);
    } else {
      if (checked) {
        onChange([...selectedValues, value]);
      } else {
        onChange(selectedValues.filter((v) => v !== value));
      }
    }
  };

  const isSelected = (value: string) => selectedValues.includes(value);

  return (
    <div className={`${className}`}>
      <h3 className="font-semibold mb-3 text-secondary-900">{title}</h3>

      <div className="space-y-2">
        {visibleOptions.map((option) => {
          const selected = isSelected(option.value);
          const inputId = `ProductFilter-${title.toLowerCase()}-${option.value}`;

          return (
            <label key={option.value} htmlFor={inputId} className="flex items-center space-x-2 cursor-pointer group hover:bg-gray-50 p-2 rounded-md">
              <input
                id={inputId}
                type={type}
                name={type === 'radio' ? `ProductFilter-${title.toLowerCase()}` : undefined}
                checked={selected}
                onChange={(e) => handleOptionChange(option.value, e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
              />
              <div className="flex-1 flex justify-between items-center">
                <span className={`text-sm ${selected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{option.label}</span>
                {option.count !== undefined && option.count > 0 && <span className="text-xs text-gray-500 ml-2">({option.count})</span>}
              </div>
            </label>
          );
        })}
      </div>

      {hasMore && (
        <button type="button" onClick={() => setShowAll(!showAll)} className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium">
          {showAll ? 'Ver menos' : `Ver todos (${options.length})`}
        </button>
      )}
    </div>
  );
};

export default ProductFilterSection;
